/** Slug de la condición de calidad que identifica una orden ODD. */
export const ODD_CONDITION_SLUG = 'odd';

/**
 * Snapshot de una silobolsa comprometida, tal como se persiste en el backend
 * (`negotiation_silobags`) y como lo espera `NegotiationSilobag::extractRefs`.
 *
 * Es lo que viaja en `negotiation.silo_bags` y lo que devuelve el backend para que
 * la contraparte muestre las bolsas comprometidas SIN volver a llamar a Siloreal.
 */
export interface SiloBagRef {
  silobag_id: string;
  silobag_uuid?: string | null;
  silobag_name?: string | null;
  species?: string | null;
  harvest?: string | null;
  organization?: string | null;
  input_weight?: number | string | null;
  remaining_stock?: number | string | null;
  lot_name?: string | null;
  countryside_name?: string | null;
  humidity?: number | string | null;
  bagged_date?: string | null;
  latitude?: number | string | null;
  longitude?: number | string | null;
}

/**
 * SiloBag representa una silobolsa individual registrada en el campo del productor.
 *
 * Se construye a partir de la respuesta del proxy backend de Siloreal
 * (`GET /companies/:id/siloreal/silobags`). Conserva los campos de display de la tabla
 * y sabe producir el snapshot (`toRef`) que se envía/persiste en la negociación.
 */
export class SiloBag {
  /** Identificador único de la silobolsa (SiloID de Siloreal). */
  silo_id: string;
  /** Tipo de grano almacenado actualmente en la bolsa (ej. soja, maiz, trigo). */
  grain_type: string;
  /** Peso en toneladas actualmente embolsado. */
  bagged_weight: number;
  /** Humedad (%) medida en el momento del embolsado. */
  humidity?: number | null;
  /** Fecha en que se selló la bolsa (YYYY-MM-DD). */
  bagged_date?: string | null;
  /** Días transcurridos desde que se selló la bolsa. */
  days_since_bagging: number;
  /** Geolocalización — latitud. */
  latitude?: number | null;
  /** Geolocalización — longitud. */
  longitude?: number | null;
  /** Longitud de la bolsa en metros. */
  length_m?: number | null;
  /**
   * ID de la orden a la que ya está comprometida esta bolsa.
   * Null significa que la bolsa está libre y puede seleccionarse para una nueva orden.
   */
  assigned_order_id?: number | null;
  /** Nombre de la bolsa en Siloreal (ej. "Bs 5"). */
  silobag_name?: string | null;
  /** Nombre del lote donde está ubicada la bolsa. */
  lot_name?: string | null;
  /** Nombre del campo/establecimiento. */
  countryside_name?: string | null;

  // Campos del snapshot que no se muestran en la tabla pero viajan al backend.
  /** UUID de la silobolsa en Siloreal. */
  silobag_uuid?: string | null;
  /** Cosecha / campaña. */
  harvest?: string | null;
  /** Organización de Siloreal dueña de la bolsa. */
  organization?: string | null;
  /** Peso embolsado de origen. */
  input_weight?: number | null;
  /** Stock remanente (kg, según Siloreal). */
  remaining_stock?: number | null;

  constructor(data: Partial<SiloBag> = {}) {
    Object.assign(this, data);
  }

  /**
   * Snapshot que se envía al backend en `negotiation.silo_bags`. El backend lo persiste
   * para que la contraparte pueda ver las bolsas comprometidas sin llamar a Siloreal.
   */
  toRef(): SiloBagRef {
    return {
      silobag_id: this.silo_id,
      silobag_uuid: this.silobag_uuid ?? null,
      silobag_name: this.silobag_name ?? null,
      species: this.grain_type ?? null,
      harvest: this.harvest ?? null,
      organization: this.organization ?? null,
      input_weight: this.input_weight ?? null,
      remaining_stock: this.remaining_stock ?? null,
      lot_name: this.lot_name ?? null,
      countryside_name: this.countryside_name ?? null,
      humidity: this.humidity ?? null,
      bagged_date: this.bagged_date ?? null,
      latitude: this.latitude ?? null,
      longitude: this.longitude ?? null,
    };
  }
}
